import { Tabs, TabsList, TabsTrigger } from '@/components/ui/animated-tabs'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { ROUTES_CONFIG } from '@/config/routes.config'
import { convertNumberToMarketValue } from '@/lib/utils'
import { useTRPC } from '@/trpc'
import { useQuery } from '@tanstack/react-query'
import React, { useState } from 'react'
import { useTypedParams } from 'react-router-typesafe-routes'
import Overview from './(components)/overview'
import Checks from './(components)/checks'
import PlayerTasks from '@/components/features/players/tasks'

const OfferedPlayerDetailPage = () => {
    const { trpc } = useTRPC()
    const { offered_player_id, contact_id } = useTypedParams(ROUTES_CONFIG.APP.CONTACTS.DETAIL.OFFERED_PLAYER_ID)
    const [tab, setTab] = useState<string>('overview');
    
    const { data:offered_player } = useQuery({
        queryKey: ['player-offered', offered_player_id],
        queryFn: async () => {
            const player = await trpc.contacts.playersOffered.search.mutate({
                id: offered_player_id
            })
            return player[0]
        }
    })

    const { data:player } = useQuery({
        queryKey: ['players', offered_player?.mongo_player_id],
        queryFn: async () => {
            if(!offered_player?.mongo_player_id) throw new Error("Player not found");
            const player = await trpc.players.getById.query({ player_id: offered_player?.mongo_player_id });
            return player;
        },
        enabled: !!offered_player?.mongo_player_id
    })

    return (
        <div className='p-4 flex flex-col gap-4 justify-between h-full'>
            <header className='flex items-center gap-10'>
                <div className='size-32 aspect-square relative'>
                    <div className='h-full w-full bg-foreground/10 rounded-full flex items-center justify-center outline-2 outline-bright outline-offset-4'>
                        <Avatar className='w-full h-full'>
                            <AvatarImage className='object-cover' src={player?.imageUrl ?? undefined} alt="" />
                            <AvatarFallback className='bg-foreground/10 text-3xl opacity-50'>{player?.name?.charAt(0)}</AvatarFallback>
                        </Avatar>
                    </div>
                    <div className='size-10 absolute -right-2 -bottom-2 aspect-square bg-foreground flex items-center justify-center rounded-full border-background border-2'>
                        <p className='text-lg text-background text-center font-medium'>{player?.shirtNumber?.replace('#','') || "#"}</p>
                    </div>
                </div>
    
                <div className='flex flex-col gap-2'>
    
                    <div className='flex items-center gap-3'>
                        <p className='text-3xl font-semibold'>{player?.name}</p>
                        <div className='h-full min-h-10 w-px bg-foreground/20'></div>
                        <div className='flex items-center gap-2'>
                            <Avatar className='size-8 aspect-square'>
                                <AvatarImage className='object-cover' src={player?.club?.imageUrl ?? undefined} alt="" />
                                <AvatarFallback className='bg-foreground/10 opacity-50'>{player?.club?.name?.charAt(0)}</AvatarFallback>
                            </Avatar>
                            <p className='text-lg'>{player?.club?.name}</p>
                        </div>
                    </div>
    
                    <div className='py-2 px-4 bg-foreground/10 rounded-md flex items-center gap-4 w-fit'>
                        <div className='flex items-center gap-3'>
                            <p className='text-sm text-foreground/50'>Leeftijd</p>
                            <p>{player?.age || "-"}</p>
                        </div>
    
                        <div className='h-full min-h-6 w-px bg-foreground/20'></div>
    
                        <div className='flex items-center gap-3'>
                            <p className='text-sm text-foreground/50'>Pos.</p>
                            <p>{player?.positionMain || "-"}</p>
                        </div>
    
                        <div className='h-full min-h-6 w-px bg-foreground/20'></div>
    
                        <div className='flex items-center gap-3'>
                            <p className='text-sm text-foreground/50'>Voet</p>
                            <p>{player?.foot || "-"}</p>
                        </div>
    
                        <div className='h-full min-h-6 w-px bg-foreground/20'></div>
    
                        <div className='flex items-center gap-3'>
                            <p className='text-sm text-foreground/50'>MW</p>
                            <p>{convertNumberToMarketValue(player?.marketValue) || "-"}</p>
                        </div>
                    </div>
    
                </div>
            </header>

            <main className='bg-foreground/10 h-full overflow-auto rounded-lg'>
                <div className='flex flex-col justify-between h-full'>
                    <div className='border-b p-2'>
                        <Tabs value={tab} onValueChange={setTab}>
                            <TabsList className='p-0'>
                                <TabsTrigger value="overview">Overzicht</TabsTrigger>
                                {offered_player?.mongo_player_id && <TabsTrigger value="tasks">Alle taken</TabsTrigger>}
                                {offered_player?.mongo_player_id && <TabsTrigger value="checks">Checks</TabsTrigger>}
                            </TabsList>
                        </Tabs>
                    </div>
                    <div className='h-full overflow-auto p-4'>
                        <div className='flex flex-col h-full'>
                            {tab === "overview" && <Overview offered_player_id={offered_player_id}/>}
                            {(tab === "tasks" && offered_player?.mongo_player_id) && <PlayerTasks mongo_player_id={offered_player?.mongo_player_id} />}
                            {(tab === "checks" && offered_player?.mongo_player_id) && <PlayerTasks mongo_player_id={offered_player?.mongo_player_id} types={['check']} />}
                        </div>
                    </div>
                </div>  
            </main>
        </div>
    )
}

export default OfferedPlayerDetailPage